/**
 * useDevUtilities - Hook for Development Utilities
 *
 * Provides access to development utilities like Lakeview quick fill.
 * Only available when in dev region.
 *
 * Usage:
 * ```tsx
 * const devUtils = useDevUtilities(projectId);
 * if (devUtils.isDev) {
 *   <Button onClick={devUtils.quickFillDates}>Fill Dates</Button>
 * }
 * ```
 */

import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useProjectSetupHandlers } from '~/hooks/useProjectSetupHandlers';
import { isDevRegion } from '~/services/dev-utilities/dev-utilities-service';
import {
  LAKEVIEW_QUICK_FILL_DATA,
  lakeview2016,
  lakeview2020,
  lakeview2022,
} from '~/services/dev-utilities/lakeview-data';
import type {
  DateInputsForm,
  DRFInputsForm,
  ExistingDebtPaymentInput,
  MillageInputsForm,
  TaxValueInputsForm,
} from '~/types';

export interface DevUtilities {
  isDev: boolean;
  quickFillDates: () => void;
  quickFillTaxValues: () => void;
  quickFillMillage: () => void;
  quickFillDRF: () => void;
  getExistingDebtTemplates: () => {
    lakeview2016: ExistingDebtPaymentInput[];
    lakeview2020: ExistingDebtPaymentInput[];
    lakeview2022: ExistingDebtPaymentInput[];
  };
}

/**
 * Hook for accessing development utilities
 * @param projectId - Optional project ID. If not provided, will try to get from route params.
 */
export const useDevUtilities = (projectId?: string): DevUtilities => {
  const params = useParams<{ id: string }>();
  const actualProjectId = projectId || params.id || '';

  // Always call hook (React rules), but handlers will only work if projectId is valid
  const handlers = useProjectSetupHandlers(actualProjectId);
  const isDev = isDevRegion();

  const quickFillDates = useCallback(() => {
    if (!isDev || !actualProjectId) return;
    const lakeviewDates: DateInputsForm = {
      startingTaxYear: LAKEVIEW_QUICK_FILL_DATA.dates.startingTaxYear,
      fiscalYearEnd: LAKEVIEW_QUICK_FILL_DATA.dates.fiscalYearEnd,
      taxCollectionSchedule: LAKEVIEW_QUICK_FILL_DATA.dates.taxCollectionSchedule,
      paymentDates: LAKEVIEW_QUICK_FILL_DATA.dates.paymentDates.map((pd) => ({ ...pd })),
    };
    handlers.handleDateInputsChange(lakeviewDates);
  }, [handlers, isDev, actualProjectId]);

  const quickFillTaxValues = useCallback(() => {
    if (!isDev || !actualProjectId) return;
    const lakeviewTax: TaxValueInputsForm = {
      initialTaxableValue: LAKEVIEW_QUICK_FILL_DATA.tax.initialTaxableValue,
      startYearGrowthRate: LAKEVIEW_QUICK_FILL_DATA.tax.startYearGrowthRate,
      fiveYearGrowthRate: LAKEVIEW_QUICK_FILL_DATA.tax.fiveYearGrowthRate,
      twentyYearGrowthRate: LAKEVIEW_QUICK_FILL_DATA.tax.twentyYearGrowthRate,
      startingTaxYear: LAKEVIEW_QUICK_FILL_DATA.tax.startingTaxYear,
    };
    handlers.handleTaxValueInputsChange(lakeviewTax);
  }, [handlers, isDev, actualProjectId]);

  const quickFillMillage = useCallback(() => {
    if (!isDev || !actualProjectId) return;
    const lakeviewMillage: MillageInputsForm = {
      peakMillage: LAKEVIEW_QUICK_FILL_DATA.millage.peakMillage,
      peakMillageDuration: LAKEVIEW_QUICK_FILL_DATA.millage.peakMillageDuration,
      minimumMillage: LAKEVIEW_QUICK_FILL_DATA.millage.minimumMillage,
      minimumMillageDuration: LAKEVIEW_QUICK_FILL_DATA.millage.minimumMillageDuration,
    };
    handlers.handleMillageInputsChange(lakeviewMillage);
  }, [handlers, isDev, actualProjectId]);

  const quickFillDRF = useCallback(() => {
    if (!isDev || !actualProjectId) return;
    const lakeviewDRF: DRFInputsForm = {
      startingBalance: LAKEVIEW_QUICK_FILL_DATA.drf.startingBalance,
      minimumBalance: LAKEVIEW_QUICK_FILL_DATA.drf.minimumBalance,
      maximumBalance: LAKEVIEW_QUICK_FILL_DATA.drf.maximumBalance,
    };
    handlers.handleDRFInputsChange(lakeviewDRF);
  }, [handlers, isDev, actualProjectId]);

  const getExistingDebtTemplates = useCallback(() => {
    return {
      lakeview2016,
      lakeview2020,
      lakeview2022,
    };
  }, []);

  return {
    isDev,
    quickFillDates,
    quickFillTaxValues,
    quickFillMillage,
    quickFillDRF,
    getExistingDebtTemplates,
  };
};
